import { set } from 'lodash';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  ApiState,
  FormErrors,
  WorkOrderFormState,
  WorkOrderFormValues,
  WorkOrderStepKey,
  WorkOrderIssue,
} from 'types/Squares/WorkOrderManagement/work.order.creation.form.types';
import { fieldMetadata } from 'views/Settings/ExternalUsers/Squares/WorkOrderManagement/WorkOrderCreationAndUpdateForm/components/meta/metadata';

const generateUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const emptyIssue = {
  partNumber: '',
  componentId: '',
  subComponentId: '',
  plannedStartDate: null,
  plannedEndDate: null,
  description: '',
  estimatedTime: 0,
  estimatedTimeUnit: 'Hours',
  actualStartDate: null,
  actualEndDate: null,
  maintenanceActionPerformed: '',
  actualTime: null,
  actualTimeUnit: 'Hours',
  partsCost: null,
  laborCost: null,
  totalCost: null,
  currencyCode: null,
};

const initialValues: WorkOrderFormValues = Object.entries(
  fieldMetadata
).reduce<WorkOrderFormValues>((acc, [key, meta]) => {
  acc[key] = meta.initialValue ?? ''; // default "" if no initial given
  return acc;
}, {});

initialValues.components = [{ ...emptyIssue, id: generateUUID() }];

const initialState: WorkOrderFormState = {
  values: initialValues,
  metadata: fieldMetadata,
  activeStep: 0,
  errors: {},
  apiState: {
    isLoading: false,
    data: null,
    error: null,
  },
};

export const workOrderFormSlice = createSlice({
  name: 'workOrderForm',
  initialState,
  reducers: {
    updateIssueField: (
      state,
      action: PayloadAction<{ index: number; key: string; value: any }>
    ) => {
      state.values.issues[action.payload.index][action.payload.key] =
        action.payload.value;
    },
    addIssue: (state) => {
      state.values.components.push({ ...emptyIssue, id: generateUUID() });
    },
    addIssues: (
      state,
      action: PayloadAction<Array<Partial<WorkOrderIssue>>>
    ) => {
      const newIssues = action.payload.map((issue) => ({
        ...emptyIssue,
        ...issue,
        id: generateUUID(),
      }));

      state.values.components.push(...newIssues);
    },

    removeIssue: (state, action: PayloadAction<string>) => {
      const idToRemove = action.payload;

      const index = state.values.components.findIndex(
        (issue: any) => issue.id === idToRemove
      );

      if (index !== -1) {
        state.values.components.splice(index, 1);
      }
    },
    updateField: (
      state,
      action: PayloadAction<{ key: string; value: any }>
    ) => {
      set(state.values, action.payload.key, action.payload.value);

      if (state.errors[action.payload.key]) {
        state.errors = Object.fromEntries(
          Object.entries(state.errors).filter(
            ([key]) => key !== action.payload.key
          )
        );
      }
    },

    updateBulk: (state, action: PayloadAction<WorkOrderFormValues>) => {
      const payload = action.payload;

      Object.entries(payload).forEach(([key, value]) => {
        set(state.values, key, value);

        if (state.errors[key]) {
          state.errors = Object.fromEntries(
            Object.entries(state.errors).filter(([errKey]) => errKey !== key)
          );
        }
      });
    },

    clearField: (state, action: PayloadAction<{ key: string }>) => {
      state.values[action.payload.key] = '';
    },

    clearStepData: (
      state,
      action: PayloadAction<{ step: WorkOrderStepKey }>
    ) => {
      for (const [key, meta] of Object.entries(state.metadata)) {
        if (meta.step === action.payload.step) {
          state.values[key] = meta.initialValue ?? '';
        }
      }
    },

    setErrors: (state, action: PayloadAction<FormErrors>) => {
      state.errors = action.payload;
    },

    clearErrors: (state) => {
      state.errors = {};
    },

    setActiveStep: (state, action: PayloadAction<number>) => {
      state.activeStep = action.payload;
    },

    setApiState: (state, action: PayloadAction<ApiState>) => {
      state.apiState = action.payload;
    },

    clearAll: () => ({
      ...initialState,
    }),
  },
});

// Export the new actions
export const {
  updateField,
  updateBulk,
  clearField,
  clearStepData,
  clearAll,
  setApiState,
  setActiveStep,
  setErrors,
  clearErrors,
  updateIssueField,
  addIssue,
  addIssues,
  removeIssue,
} = workOrderFormSlice.actions;

export default workOrderFormSlice.reducer;
